import { useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import type { Session as SupaSession } from "@supabase/supabase-js";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { claimReferral } from "./growth.functions";
import { syncProfile } from "@/lib/board.functions";
import { isAdminEmail } from "@/lib/admin-emails";

/** Login Google selalu tersedia (dikelola Lovable Cloud). */
export const authEnabled = true;

/** Kompatibilitas: provider tidak lagi dibutuhkan, cukup passthrough. */
export function AuthProvider({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

export type Session = {
  enabled: boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  subject: string | null;
  email: string | null;
  name: string | null;
  picture: string | null;
  isAdmin: boolean;
  getIdToken: () => Promise<string | null>;
  login: () => void;
  logout: () => void;
};

export function useSession(): Session {
  const queryClient = useQueryClient();
  const [supaSession, setSupaSession] = useState<SupaSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let active = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSupaSession(data.session);
      setIsLoading(false);
    });
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, next) => {
      setSupaSession(next);
      setIsLoading(false);
    });
    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  const logout = useCallback(() => {
    // Bersihkan cache data privat sebelum sesi dihapus agar kembali ke tampilan publik.
    void queryClient.cancelQueries();
    queryClient.clear();
    void supabase.auth.signOut();
  }, [queryClient]);

  const login = useCallback(() => {
    void lovable.auth
      .signInWithOAuth("google", { redirect_uri: window.location.origin })
      .then((result) => {
        if (result.error) {
          toast.error("Gagal masuk dengan Google. Coba lagi.");
        }
      });
  }, []);

  const user = supaSession?.user ?? null;
  const email = user?.email?.toLowerCase() ?? null;
  const meta = (user?.user_metadata ?? {}) as Record<string, unknown>;

  return {
    enabled: true,
    isLoading,
    isAuthenticated: Boolean(user),
    subject: user?.id ?? null,
    email,
    name:
      (typeof meta["full_name"] === "string" && meta["full_name"]) ||
      (typeof meta["name"] === "string" && meta["name"]) ||
      null,
    picture: typeof meta["avatar_url"] === "string" ? meta["avatar_url"] : null,
    isAdmin: Boolean(user) && isAdminEmail(email),
    getIdToken: async () => (await supabase.auth.getSession()).data.session?.access_token ?? null,
    login,
    logout,
  };
}

/** Menyalin profil (subject/nama/email) ke database saat login pertama kali. */
export function useProfileSync(session: Session) {
  const sync = useServerFn(syncProfile);
  const done = useRef<string | null>(null);

  useEffect(() => {
    if (!session.isAuthenticated || !session.subject) return;
    if (done.current === session.subject) return;
    done.current = session.subject;
    void sync({
      data: {
        subject: session.subject,
        ...(session.email ? { email: session.email } : {}),
        ...(session.name ? { name: session.name } : {}),
        ...(session.picture ? { picture: session.picture } : {}),
      },
    }).catch(() => {
      done.current = null;
    });
  }, [sync, session.isAuthenticated, session.subject, session.email, session.name, session.picture]);

  useReferralCapture(session);
}

const REF_KEY = "jasasebar:ref";

/** Menyimpan kode referral dari URL (?ref=KODE) lalu mengklaimnya setelah pengguna masuk. */
function useReferralCapture(session: Session) {
  const claim = useServerFn(claimReferral);
  const done = useRef(false);

  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("ref");
    if (code) localStorage.setItem(REF_KEY, code.toUpperCase().slice(0, 24));
  }, []);

  useEffect(() => {
    if (!session.isAuthenticated || done.current) return;
    const code = localStorage.getItem(REF_KEY);
    if (!code) return;
    done.current = true;
    void (async () => {
      const idToken = await session.getIdToken();
      if (!idToken) return;
      await claim({ data: { idToken, code } }).catch(() => {});
      localStorage.removeItem(REF_KEY);
    })();
  }, [claim, session]);
}
